/**
 * 이미지 썸네일 기반 게시물 목록 컴포넌트
 * 포토갤러리 등 이미지 중심 게시판에 사용됩니다.
 * 검색, 페이지네이션, 공개글 고정 표시 기능을 제공합니다.
 */

import styled from 'styled-components';
import { useState, useEffect, useMemo, useCallback } from 'react';
import PageNav from './PageNav';
import SearchBar from './board/SearchBar';
import ImageListItem from './board/ImageListItem';

/**
 * ImageItemList 메인 컴포넌트
 * @param {Object} props
 * @param {Array} props.items - 전체 게시물 배열
 * @param {string} props.order - 상세 페이지 경로 (예: "/photo/")
 */
function ImageItemList({ items, order }) {
  const offset = 9; // 페이지당 게시물 수

  const notices = useMemo(
    () => items.filter((it) => it.isPublic === true),
    [items]
  );
  const normals = useMemo(() => items.filter((it) => !it.isPublic), [items]);

  const [isSearch, setIsSearch] = useState(false);
  const [result, setResult] = useState(items);
  const [currentPage, setCurrentPage] = useState(1);

  const totalLength = useMemo(
    () => (isSearch ? result.length : items.length),
    [isSearch, result.length, items.length]
  );

  const pinnedCount = useMemo(
    () => (isSearch ? result.filter((v) => v.isPublic).length : notices.length),
    [isSearch, result, notices.length]
  );

  const pageCount = useMemo(() => {
    const pageBody = Math.max(offset - pinnedCount, 1);
    const normalLen = (isSearch ? result : items).filter(
      (v) => !v.isPublic
    ).length;
    return Math.max(Math.ceil(normalLen / pageBody), 1);
  }, [items, result, isSearch, pinnedCount, offset]);

  const runSearch = useCallback(
    (search, mode) => {
      if (!search) {
        setIsSearch(false);
        setResult(items);
        setCurrentPage(1);
        return;
      }

      const base = [...notices]; // 공지 항상 포함
      const filtered = normals.filter((it) => {
        const t = it.title ?? '';
        const s = it.summary ?? '';
        if (mode === 1) return t.includes(search) || s.includes(search);
        if (mode === 2) return t.includes(search);
        if (mode === 3) return s.includes(search);
        return false;
      });

      setIsSearch(true);
      setResult([...base, ...filtered]);
      setCurrentPage(1);
    },
    [items, notices, normals]
  );

  useEffect(() => {
    if (!isSearch) {
      setResult(items);
    } else {
      const idSet = new Set(items.map((v) => v.id));
      setResult((prev) => prev.filter((v) => idSet.has(v.id)));
    }
  }, [items, isSearch]);

  const pageItems = useMemo(() => {
    const source = isSearch ? result : items;
    const pin = source.filter((v) => v.isPublic);
    const normal = source.filter((v) => !v.isPublic);

    const bodySize = Math.max(offset - pin.length, 1);
    if (currentPage === 1) {
      return [...pin, ...normal.slice(0, bodySize)];
    }

    const start = bodySize * (currentPage - 1);
    const end = start + bodySize;
    return [...pin, ...normal.slice(start, end)];
  }, [isSearch, result, items, offset, currentPage]);

  return (
    <Wrapper>
      <TopBar>
        <SearchBar onSearch={runSearch} />
        <StateText>
          전체 게시물 {totalLength}개, 현재 {currentPage}페이지 / 전체{' '}
          {pageCount}페이지
        </StateText>
      </TopBar>

      <Table>
        {pageItems.map((it) => (
          <li key={it.id}>
            <ImageListItem item={it} order={order} />
          </li>
        ))}
      </Table>

      <PaginationWrap>
        <PageNav cnt={pageCount} page={currentPage} change={setCurrentPage} />
      </PaginationWrap>
    </Wrapper>
  );
}

export default ImageItemList;

/* Styled Components */

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 767px) {
    padding: 0 5%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
  }
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const StateText = styled.h3`
  font-size: 20px;
  color: #333333;
  line-height: 33px;
  font-family: 'koPubWorld DotumB';
  margin: 0;
  flex: 1;

  @media (max-width: 767px) {
    display: none;
  }
`;

/* flex 기반 그리드 레이아웃 */
const Table = styled.ul`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  border-top: 2px solid #c8c8c8;
  border-bottom: 2px solid #c8c8c8;

  box-sizing: border-box;
  list-style: none;
  padding: 0;
  padding-top: 50px;
  padding-bottom: 65px;
  margin: 20px 0 0;
  gap: 0;
  @media (max-width: 767px) {
    padding-top: 30px;
    padding-bottom: 65px;
  }

  @media (max-width: 425px) {
    padding-top: 20px;
    padding-bottom: 50px;
  }
  > li {
    width: calc(33.333% - 2%);
    margin-right: 3%;
    box-sizing: border-box;
    display: flex;

    &:nth-child(3n) {
      margin-right: 0;
    }

    @media (max-width: 767px) {
      width: 49%;
      margin-right: 2%;
      &:nth-child(2n) {
        margin-right: 0;
      }
    }

    @media (max-width: 425px) {
      width: 100%;

      margin-right: 0;
    }
  }

  * {
    box-sizing: border-box;
  }
`;

const PaginationWrap = styled.div`
  width: 100%;
  margin: 20px 0;
`;
